import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, Info, Upload } from 'lucide-react';
import { apiFetch, API_BASE, handleUnauthorized } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { FilePickerField } from '@/components/FilePickerField';
import { DOCUMENT_ACCEPT, DOCUMENT_FORMAT_HINT } from '@/lib/document-formats';
import type { DocumentSummary } from './DocumentsPage';

/**
 * `/documents/:id` (v0.4 追加要件⑰): 文書の詳細 — 版一覧 + プレビュー + (manage) 未読者。
 * 設計: docs/design/v04-document-library-detail.md §6-2
 *
 * ⚠️⚠️ **プレビューは `<iframe src="https://api...">` にできない** — ブラウザは iframe に
 *  `Authorization` ヘッダを付けられないため。`fetch` (Authorization 付き) → `res.blob()` →
 *  `URL.createObjectURL` → `<iframe src={blobUrl}>` が唯一成立する形
 *  (`AiOcrReviewPage.tsx:85` の既存パターンと同じ)。
 *
 * ⚠️⚠️ **`blob:` URL は生成したページのオリジンを継承する。** そのため
 *  「実際に描画される経路を守っているのはサーバー側の MIME 許可リストだけ」であり、
 *  レスポンスヘッダ (CSP / nosniff / Content-Disposition) は blob 化した時点で失われる
 *  (security-reviewer M-1)。
 *
 * ⚠️⚠️⚠️ **PDF は `<iframe sandbox>` では描画できない (2026-09-02 に実機で確認)。**
 *  `sandbox=""` / `allow-same-origin` / `allow-scripts` / 両方、の4通りを並べて比較したところ
 *  **sandbox 属性が付いている限りどの値でも真っ白**で、属性が無いときだけ Chrome 内蔵の
 *  PDF ビューアが描画した。⇒ sandbox を付けたままだと**全 PDF が空白の箱になり機能が成立しない**。
 *  ⇒ **形式ごとに描画方法を分ける**:
 *    - 画像 → `<img>` (スクリプトを実行しえないため sandbox は不要。iframe も使わない)
 *    - PDF  → `<iframe>` (sandbox なし。⚠️ **防壁はサーバー側の MIME 許可リスト**であり、
 *             そこから `text/html` と `image/svg+xml` を除外していることが唯一かつ必須の防御)
 *  ⚠️ **許可リストに実行可能な形式を足すときは、この判断ごと見直すこと。**
 */

type VersionRow = {
  version_no: number;
  original_filename: string;
  content_type: string;
  inline_viewable: boolean;
  byte_size: number;
  change_note: string | null;
  created_at: string;
  /** ⚠️ 現状は user_id の生値。氏名解決は未実装 (D-434 の証跡としては「誰が改定したか」が要るため次段階) */
  created_by: string | null;
};

type DetailResponse = {
  can_manage: boolean;
  supports_visibility_conditions: boolean;
  document: DocumentSummary;
  versions: VersionRow[];
};

type Readership = {
  version_no: number;
  audience_count: number;
  read_count: number;
  /** 未読の総数。⚠️ `unread` は表示上限で切られるため、件数はこちらを使うこと */
  unread_count: number;
  unread_truncated: boolean;
  unread: { user_id: string; employee_id: string | null; display_name: string | null }[];
};

const contentUrl = (documentId: string, versionNo: number) =>
  `${API_BASE}/documents/${documentId}/versions/${versionNo}/content`;

/** ⚠️ `download` を付けると閲覧記録が `download` として残る (設計 §5 step9)。
 *  付けないと `view`。**サーバーが download_allowed から導出してはならない** — それだと
 *  ダウンロード可の文書はプレビューしただけで 'download' と記録され、記録の意味が壊れる。 */
const fetchContent = async (documentId: string, versionNo: number, forDownload = false): Promise<Blob> => {
  const res = await fetch(`${contentUrl(documentId, versionNo)}${forDownload ? '?download=1' : ''}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` },
  });
  handleUnauthorized(res.status);
  if (!res.ok) throw new Error('ファイルを取得できませんでした');
  return res.blob();
};

function Preview({ documentId, version }: { documentId: string; version: VersionRow }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!version.inline_viewable) return undefined;
    let revoked = '';
    let cancelled = false;
    void (async () => {
      try {
        const blob = await fetchContent(documentId, version.version_no);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoked = url;
        setSrc(url);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    // ⚠️ revokeObjectURL を忘れるとブラウザにファイルが残り続ける (メモリリーク)
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [documentId, version.version_no, version.inline_viewable]);

  if (!version.inline_viewable) {
    return (
      <p className="flex items-start gap-2 text-body">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-chrome-base" aria-hidden />
        <span>
          この形式 ({version.original_filename.split('.').pop()?.toUpperCase()}) はブラウザ内で表示できないため、
          開くと端末に保存されます。
        </span>
      </p>
    );
  }
  if (failed) return <p className="text-body text-alert-text">プレビューを表示できませんでした。</p>;
  if (!src) return <p className="text-body text-text-sub">プレビューを読み込み中…</p>;

  // 画像は <img> で描画する。スクリプトを実行しえないので iframe に入れる理由が無い。
  if (version.content_type.startsWith('image/')) {
    return (
      <img src={src} alt={`${version.original_filename} のプレビュー`}
        className="max-w-full h-auto border border-surface-border rounded bg-white" />
    );
  }
  // PDF。⚠️ sandbox を付けると (どの値でも) Chrome 内蔵ビューアが描画しない — 上のコメント参照。
  return (
    <iframe
      src={src}
      title={`${version.original_filename} のプレビュー`}
      className="w-full h-[60vh] min-h-[320px] border border-surface-border rounded bg-white"
    />
  );
}

export default function DocumentDetailPage() {
  const { documentId = '' } = useParams();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const { data, isLoading, isError } = useQuery<DetailResponse>({
    queryKey: ['documents', documentId],
    queryFn: () => apiFetch<DetailResponse>(`/documents/${documentId}`),
    enabled: documentId !== '',
  });

  const versions = data?.versions ?? [];
  const current = versions.find((v) => v.version_no === (selected ?? data?.document.current_version_no)) ?? versions[0];

  const { data: readership, isLoading: readershipLoading, isError: readershipError } = useQuery<Readership>({
    queryKey: ['documents', documentId, 'readership', current?.version_no],
    queryFn: () => apiFetch<Readership>(`/documents/${documentId}/readership?version_no=${current!.version_no}`),
    enabled: (data?.can_manage ?? false) && current !== undefined,
  });

  const revise = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('ファイルを選択してください');
      const fd = new FormData();
      if (changeNote) fd.set('change_note', changeNote);
      fd.set('file', file);
      const res = await fetch(`${API_BASE}/documents/${documentId}/versions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` },
        body: fd,
      });
      handleUnauthorized(res.status);
      const body = await res.json() as { success: boolean; error?: { message: string } };
      if (!res.ok || !body.success) throw new Error(body.error?.message ?? '改定に失敗しました');
    },
    onSuccess: () => {
      toast.success('新しい版を登録しました');
      setReviseOpen(false);
      setChangeNote('');
      setFile(null);
      setSelected(null);
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const download = async () => {
    if (!current) return;
    try {
      const blob = await fetchContent(documentId, current.version_no, true);
      const url = URL.createObjectURL(blob);
      // ⚠️ QA MEDIUM-3: peer (lib/csv-export.ts) と同じく DOM へ追加してからクリックする。
      //    追加せずに click() すると一部ブラウザでダウンロードが始まらない。
      const a = document.createElement('a');
      a.href = url;
      a.download = current.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('ファイルを取得できませんでした');
    }
  };

  if (isLoading) return <p className="text-body text-text-sub">読み込み中…</p>;
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <Link to="/documents" className="text-body text-chrome-base underline">
          <ArrowLeft className="w-4 h-4 inline mr-1 -mt-0.5" aria-hidden />社内文書へ戻る
        </Link>
        <p className="text-body text-alert-text">この文書は表示できません。</p>
      </div>
    );
  }

  const doc = data.document;
  // ⚠️ 「ダウンロードを禁止しています」と断言しない (D-433)。ブラウザ内蔵 PDF ビューアの保存や
  //    スクリーンショットは原理的に抑止できず、断言すると利用者に誤った安心を与える。
  const showDownload = doc.download_allowed || (current !== undefined && !current.inline_viewable);

  return (
    <div className="space-y-4">
      <Link to="/documents" className="text-body text-chrome-base underline inline-block min-h-[44px] py-2">
        <ArrowLeft className="w-4 h-4 inline mr-1 -mt-0.5" aria-hidden />社内文書へ戻る
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="text-h1 break-words">{doc.title}</h1>
          {doc.description && <p className="mt-1 text-body text-muted break-words">{doc.description}</p>}
        </div>
        <div className="flex gap-2 shrink-0">
          {showDownload && (
            <Button variant="secondary" onClick={() => void download()} className="min-h-[44px]">
              <Download className="w-4 h-4 mr-1.5" aria-hidden />ダウンロード
            </Button>
          )}
          {data.can_manage && (
            <Button onClick={() => setReviseOpen(true)} className="min-h-[44px]">
              <Upload className="w-4 h-4 mr-1.5" aria-hidden />改定
            </Button>
          )}
        </div>
      </div>

      {!showDownload && (
        // ⚠️ D-433: 「ダウンロードを禁止しています」と断言しない (§2-6 の限界があり誤った安心を与える)。
        // ⚠️ design-expert H-3: 法的含意のある注記を最小サイズ・最低コントラストで出さない。
        <p className="text-body text-text-sub">
          この文書はダウンロード用に提供していません。
          ただし、ブラウザの PDF ビューアの保存機能やスクリーンショットまでは防げません。
        </p>
      )}

      {current && <Preview documentId={documentId} version={current} />}

      <section className="border border-surface-border rounded-lg bg-white p-4 overflow-x-auto">
        <h2 className="text-h2">版の履歴</h2>
        <table className="mt-2 w-full text-body">
          <thead>
            <tr className="border-b border-surface-border text-left">
              <th scope="col" className="relative py-2 pr-3 whitespace-nowrap">版</th>
              <th scope="col" className="relative py-2 pr-3">改定メモ</th>
              <th scope="col" className="relative py-2 pr-3 whitespace-nowrap">登録日</th>
              <th scope="col" className="relative py-2 pr-3 whitespace-nowrap">登録者</th>
              <th scope="col" className="relative py-2 whitespace-nowrap">表示</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.version_no} className="border-b border-surface-border last:border-0">
                <td className="py-2 pr-3 whitespace-nowrap">第{v.version_no}版</td>
                <td className="py-2 pr-3 break-words">{v.change_note ?? '—'}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{v.created_at.slice(0, 10)}</td>
                <td className="py-2 pr-3 break-all">{v.created_by ?? '—'}</td>
                <td className="py-2 whitespace-nowrap">
                  <Button variant="secondary" className="min-h-[44px]"
                    onClick={() => setSelected(v.version_no)}
                    aria-label={`第${v.version_no}版を表示`}>
                    {current?.version_no === v.version_no ? '表示中' : '表示'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {data.can_manage && current && (
        <section className="border border-surface-border rounded-lg bg-white p-4">
          <h2 className="text-h2">第{current.version_no}版の閲覧状況</h2>
          {/* ⚠️ design-expert M-1: 取得失敗時に節ごと消すと「未読0名」と誤解される。必ず状態を出す。 */}
          {readershipLoading && <p className="mt-1 text-body text-text-sub">読み込み中…</p>}
          {readershipError && (
            <p className="mt-1 text-body text-alert-text">閲覧状況を取得できませんでした。</p>
          )}
          {readership && (<>
          <p className="mt-1 text-body">
            対象 {readership.audience_count} 名中 {readership.read_count} 名が閲覧済み
          </p>
          {/* ⚠️ 「周知義務を満たす」等の法的な充足を主張しない (D-434)。
              「閲覧した」は「読んで理解した」ではない。 */}
          <p className="mt-1 text-body text-text-sub">
            この記録は文書を開いたことを示すもので、内容の理解や同意を示すものではありません。
          </p>
          {readership.unread_count > 0 ? (<>
            <p className="mt-2 text-body">未閲覧 {readership.unread_count} 名</p>
            <ul className="mt-1 space-y-1">
              {readership.unread.map((u) => (
                <li key={u.user_id} className="text-body break-words">{u.display_name ?? u.user_id}</li>
              ))}
            </ul>
            {/* ⚠️ QA HIGH-3: 氏名の解決は D1 の SQL 変数上限のため上限で切る。切ったことを必ず伝える */}
            {readership.unread_truncated && (
              <p className="mt-1 text-body text-text-sub">
                ここに表示しているのは未閲覧者のうち先頭 {readership.unread.length} 名です。
              </p>
            )}
          </>) : (
            <p className="mt-2 text-body text-text-sub">未閲覧の対象者はいません。</p>
          )}
          </>)}
        </section>
      )}

      <Modal open={reviseOpen} onClose={() => setReviseOpen(false)} title="新しい版を登録">
        <div className="space-y-3">
          <p className="text-body text-text-sub">
            新しい版を登録すると、この文書の閲覧状況は新しい版に対して数え直されます
            (前の版を読んだ人は「未閲覧」に戻ります)。
          </p>
          <label className="block">
            <span className="text-caption text-muted">改定メモ</span>
            <input className="mt-1 w-full min-h-[44px] border border-surface-border rounded px-3"
              placeholder="2026-04 改定" value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
          </label>
          <FilePickerField label="ファイル" required accept={DOCUMENT_ACCEPT}
            hint={DOCUMENT_FORMAT_HINT} onChange={setFile} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setReviseOpen(false)}>キャンセル</Button>
            <Button onClick={() => revise.mutate()} disabled={revise.isPending || !file}>
              {revise.isPending ? '登録中…' : '登録'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
